// src/pages/Organizations/Marketing/components/MarketingAnalytics.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  Tab,
  Tabs,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  CartesianGrid,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useMarketingStudio, Campaign, MarketingAsset } from '../../../../contexts/MarketingStudioContext';

interface MarketingAnalyticsProps {
  organizationId: string;
  organizationName: string;
}

// Mock data for analytics
const generateMockAnalyticsData = (campaigns: Campaign[], organizationId: string) => {
  // Filter campaigns by organization
  const orgCampaigns = campaigns.filter(campaign => campaign.organizationId === organizationId);
  
  // Summary metrics
  const summary = {
    totalCampaigns: orgCampaigns.length,
    activeCampaigns: orgCampaigns.filter(c => c.status === 'active').length,
    completedCampaigns: orgCampaigns.filter(c => c.status === 'completed').length,
    totalImpressions: Math.floor(Math.random() * 100000) + 5000,
    totalClicks: Math.floor(Math.random() * 10000) + 500,
    totalConversions: Math.floor(Math.random() * 1000) + 50,
    ctr: 0, // Initialize with default values
    conversionRate: 0
  };
  
  // Calculate CTR and conversion rate
  summary.ctr = summary.totalClicks / summary.totalImpressions * 100;
  summary.conversionRate = summary.totalConversions / summary.totalClicks * 100;
  
  // Performance by platform
  const platformData: Array<{ 
    name: string; 
    impressions: number; 
    clicks: number; 
    conversions: number;
    ctr?: string; 
  }> = [
    { name: 'Facebook', impressions: Math.floor(Math.random() * 30000) + 3000, clicks: Math.floor(Math.random() * 3000) + 300, conversions: Math.floor(Math.random() * 300) + 30 },
    { name: 'Instagram', impressions: Math.floor(Math.random() * 40000) + 4000, clicks: Math.floor(Math.random() * 4000) + 400, conversions: Math.floor(Math.random() * 400) + 40 },
    { name: 'Twitter', impressions: Math.floor(Math.random() * 20000) + 2000, clicks: Math.floor(Math.random() * 2000) + 200, conversions: Math.floor(Math.random() * 200) + 20 },
    { name: 'LinkedIn', impressions: Math.floor(Math.random() * 10000) + 1000, clicks: Math.floor(Math.random() * 1000) + 100, conversions: Math.floor(Math.random() * 100) + 10 },
    { name: 'Email', impressions: Math.floor(Math.random() * 25000) + 2500, clicks: Math.floor(Math.random() * 2500) + 250, conversions: Math.floor(Math.random() * 250) + 25 },
  ];
  
  // Calculate CTR for each platform
  platformData.forEach(platform => {
    platform.ctr = (platform.clicks / platform.impressions * 100).toFixed(2);
  });
  
  // Timeline data (last 30 days)
  const timelineData = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    timelineData.push({
      date: date.toISOString().split('T')[0],
      impressions: Math.floor(Math.random() * 5000) + 500,
      clicks: Math.floor(Math.random() * 500) + 50,
      conversions: Math.floor(Math.random() * 50) + 5,
    });
  }
  
  // Campaign performance
  const campaignPerformance = orgCampaigns.map(campaign => {
    return {
      id: campaign.id,
      name: campaign.name,
      status: campaign.status,
      impressions: Math.floor(Math.random() * 20000) + 1000,
      clicks: Math.floor(Math.random() * 2000) + 100,
      conversions: Math.floor(Math.random() * 200) + 10,
      ctr: ((Math.random() * 5) + 1).toFixed(2),
      conversionRate: ((Math.random() * 10) + 1).toFixed(2),
    };
  });
  
  // Top assets
  const topAssets = [
    { id: '1', name: 'Summer Promotion Banner', type: 'image', impressions: 15245, clicks: 1245, ctr: '8.17' },
    { id: '2', name: 'Product Launch Video', type: 'video', impressions: 12370, clicks: 987, ctr: '7.98' },
    { id: '3', name: 'Holiday Special', type: 'image', impressions: 9856, clicks: 723, ctr: '7.34' },
    { id: '4', name: 'Customer Testimonial', type: 'content', impressions: 7645, clicks: 534, ctr: '6.99' },
    { id: '5', name: 'Brand Story Email', type: 'content', impressions: 6543, clicks: 432, ctr: '6.60' },
  ];
  
  return {
    summary,
    platformData,
    timelineData,
    campaignPerformance,
    topAssets,
  };
};

const MarketingAnalytics: React.FC<MarketingAnalyticsProps> = ({ organizationId, organizationName }) => {
  const theme = useTheme();
  const { campaigns, marketingAssets } = useMarketingStudio();
  
  // Analytics state
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [tabValue, setTabValue] = useState(0);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  
  // Generate mock analytics data
  useEffect(() => {
    setAnalyticsData(generateMockAnalyticsData(campaigns, organizationId));
  }, [campaigns, organizationId]);
  
  // Handle date range change
  const handleDateRangeChange = (event: SelectChangeEvent) => {
    setDateRange(event.target.value as '7d' | '30d' | '90d' | '1y');
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  if (!analyticsData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Loading analytics data...</Typography>
      </Box>
    );
  }
  
  // Colors for charts
  const platformColors = ['#2196F3', '#FF9800', '#4CAF50', '#F44336', '#9C27B0'];
  
  return (
    <Box>
      {/* Analytics Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          aria-label="analytics tabs"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Campaigns" />
          <Tab label="Platform" />
          <Tab label="Content" />
        </Tabs>
        
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="date-range-label">Date Range</InputLabel>
          <Select
            labelId="date-range-label"
            id="date-range"
            value={dateRange}
            label="Date Range"
            onChange={handleDateRangeChange}
            size="small"
          >
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
            <MenuItem value="1y">Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {/* Overview Tab */}
      {tabValue === 0 && (
        <Box>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Impressions
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {analyticsData.summary.totalImpressions.toLocaleString()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                    <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      +12.5% from previous period
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Clicks
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {analyticsData.summary.totalClicks.toLocaleString()}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                    <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      +8.3% from previous period
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Click-through Rate
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {analyticsData.summary.ctr.toFixed(2)}%
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                    <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      -1.2% from previous period
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%', 
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Conversion Rate
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {analyticsData.summary.conversionRate.toFixed(2)}%
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                    <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      +3.7% from previous period
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Engagement Over Time */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="h6" gutterBottom>
              Engagement Over Time
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={analyticsData.timelineData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="impressions" stroke="#2196F3" activeDot={{ r: 8 }} />
                  <Line type="monotone" dataKey="clicks" stroke="#FF9800" />
                  <Line type="monotone" dataKey="conversions" stroke="#4CAF50" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
          
          {/* Platform Performance & Top Assets */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Platform Performance
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analyticsData.platformData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="impressions" name="Impressions" fill="#2196F3" />
                      <Bar dataKey="clicks" name="Clicks" fill="#FF9800" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography variant="h6" gutterBottom>
                  CTR by Platform
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        dataKey="clicks"
                        nameKey="name"
                        data={analyticsData.platformData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        label={(entry) => `${entry.name}: ${entry.ctr}%`}
                      >
                        {analyticsData.platformData.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={platformColors[index % platformColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {/* Campaign Tab */}
      {tabValue === 1 && (
        <Box>
          {/* Campaign Status Summary */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Campaigns
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {analyticsData.summary.totalCampaigns}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Active Campaigns
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {analyticsData.summary.activeCampaigns}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Completed Campaigns
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {analyticsData.summary.completedCampaigns}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                borderRadius: 2,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`
              }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Average CTR
                  </Typography>
                  <Typography variant="h4" sx={{ my: 1 }}>
                    {analyticsData.summary.ctr.toFixed(2)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Campaign Performance Table */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box sx={{ p: 3, pb: 1 }}>
              <Typography variant="h6">
                Campaign Performance
              </Typography>
            </Box>
            <Divider />
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">CTR</TableCell>
                    <TableCell align="right">Conversions</TableCell>
                    <TableCell align="right">Conv. Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsData.campaignPerformance.map((campaign: any) => (
                    <TableRow key={campaign.id}>
                      <TableCell>{campaign.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)} 
                          size="small"
                          sx={{ 
                            bgcolor: 
                              campaign.status === 'active' ? '#4caf50' :
                              campaign.status === 'scheduled' ? '#2196f3' :
                              campaign.status === 'completed' ? '#8bc34a' :
                              campaign.status === 'archived' ? '#757575' : '#9e9e9e',
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">{campaign.impressions.toLocaleString()}</TableCell>
                      <TableCell align="right">{campaign.clicks.toLocaleString()}</TableCell>
                      <TableCell align="right">{campaign.ctr}%</TableCell>
                      <TableCell align="right">{campaign.conversions.toLocaleString()}</TableCell>
                      <TableCell align="right">{campaign.conversionRate}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
      
      {/* Platform Tab */}
      {tabValue === 2 && (
        <Box>
          {/* Platform Comparison */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="h6" gutterBottom>
              Platform Comparison
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analyticsData.platformData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="impressions" name="Impressions" fill="#2196F3" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analyticsData.platformData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="clicks" name="Clicks" fill="#FF9800" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Platform Metrics Table */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box sx={{ p: 3, pb: 1 }}>
              <Typography variant="h6">
                Platform Metrics
              </Typography>
            </Box>
            <Divider />
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Platform</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">CTR</TableCell>
                    <TableCell align="right">Conversions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsData.platformData.map((platform: any) => (
                    <TableRow key={platform.name}>
                      <TableCell>{platform.name}</TableCell>
                      <TableCell align="right">{platform.impressions.toLocaleString()}</TableCell>
                      <TableCell align="right">{platform.clicks.toLocaleString()}</TableCell>
                      <TableCell align="right">{platform.ctr}%</TableCell>
                      <TableCell align="right">{platform.conversions.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}
      
      {/* Content Tab */}
      {tabValue === 3 && (
        <Box>
          {/* Top Performing Assets */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              mb: 3,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box sx={{ p: 3, pb: 1 }}>
              <Typography variant="h6">
                Top Performing Assets
              </Typography>
            </Box>
            <Divider />
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Asset</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">CTR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsData.topAssets.map((asset: any) => (
                    <TableRow key={asset.id}>
                      <TableCell>{asset.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={asset.type} 
                          size="small" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell align="right">{asset.impressions.toLocaleString()}</TableCell>
                      <TableCell align="right">{asset.clicks.toLocaleString()}</TableCell>
                      <TableCell align="right">{asset.ctr}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* Content Type Performance */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="h6" gutterBottom>
              Content Type Performance
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { name: 'Images', ctr: 7.2, convRate: 3.1 },
                    { name: 'Videos', ctr: 8.4, convRate: 4.2 },
                    { name: 'Text', ctr: 5.1, convRate: 2.3 },
                    { name: 'Interactive', ctr: 9.2, convRate: 5.4 },
                  ]}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="ctr" name="CTR (%)" fill="#2196F3" />
                  <Bar dataKey="convRate" name="Conversion Rate (%)" fill="#4CAF50" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default MarketingAnalytics;
